/**
 * useUserSettings -- React hook for intent-based user settings mutations.
 *
 * Endpoint:
 * - PATCH /api/streaming/users/:serverType/:userId/settings (CSRF protected)
 *
 * Sends a partial object with intent fields (disabled, hidden, remoteAccess, etc.)
 * and receives per-intent results.
 */
import { useCallback, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Types
// ============================================================================

export interface SettingsResult {
  results: Array<{
    intentId: string;
    ok: boolean;
    applied?: unknown;
    message?: string;
  }>;
}

/** Intent fields accepted by the PATCH endpoint. All optional. */
export interface UserSettingsPatch {
  // Account
  disabled?: boolean;
  hidden?: boolean;
  hiddenRemotely?: boolean;
  hiddenFromNewDevices?: boolean;
  // Streaming
  remoteAccess?: boolean;
  allowPlayback?: boolean;
  simultaneousStreams?: number;
  bitrateLimit?: number;
  allDevices?: boolean;
  // Transcoding
  videoTranscoding?: boolean;
  audioTranscoding?: boolean;
  remuxing?: boolean;
  syncTranscoding?: boolean;
  mediaConversion?: boolean;
  autoRemoteQuality?: number;
  // Library
  allFolders?: boolean;
  enabledFolders?: string[];
  excludedSubFolders?: string[];
  allChannels?: boolean;
  // Content
  allowDeletion?: boolean;
  allowDownload?: boolean;
  subtitleDownloading?: boolean;
  subtitleManagement?: boolean;
  // Parental
  maxParentalRating?: number;
  blockedTags?: string[];
  includeTags?: string[];
  tagBlockingInclusive?: boolean;
  allowTagOrRating?: boolean;
  blockUnratedItems?: string[];
  // Social
  publicSharing?: boolean;
  sharePersonalItems?: boolean;
  cameraUpload?: boolean;
  // Live TV
  liveTvAccess?: boolean;
  liveTvManagement?: boolean;
  // Features
  remoteControlOthers?: boolean;
  sharedDeviceControl?: boolean;
  userPreferenceAccess?: boolean;
  restrictedFeatures?: string[];
  // Config (preferences)
  audioLanguage?: string;
  subtitleLanguage?: string;
  subtitleMode?: string;
  playDefaultAudio?: boolean;
  showMissingEpisodes?: boolean;
  autoPlayNext?: boolean;
  hidePlayedInLatest?: boolean;
  rememberAudio?: boolean;
  rememberSubtitles?: boolean;
  introSkipMode?: string;
  // WSD enforcement
  bypassKillStream?: boolean;
}

// ============================================================================
// useUserSettings
// ============================================================================

export function useUserSettings(): {
  updating: boolean;
  updateSettings: (
    serverType: string,
    userId: string,
    patch: UserSettingsPatch
  ) => Promise<SettingsResult | null>;
} {
  const [updating, setUpdating] = useState(false);

  const updateSettings = useCallback(
    async (
      serverType: string,
      userId: string,
      patch: UserSettingsPatch
    ): Promise<SettingsResult | null> => {
      setUpdating(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}/settings`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify(patch),
        });

        const json = await res.json();

        if (!res.ok || !json.ok || !json.data?.results) {
          throw new Error(json.message ?? "Failed to update user settings");
        }

        return json.data as SettingsResult;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update user settings";
        throw new Error(message);
      } finally {
        setUpdating(false);
      }
    },
    []
  );

  return { updating, updateSettings };
}
